import { createAsyncThunk, createSlice, isFulfilled } from '@reduxjs/toolkit'
import { RootState, store } from '../../app/store'
import { getAPIURL } from '../../GlobalComponents/URLs'
// import { store } from '../../app/store'
import { fqAlert } from '../../GlobalComponents/NotificationManager/NotificationManager'

const ORDER_ENTRY_APPNAME = 'ORDERENTRY'
const RENEW_EVERY_X_SEC = 45 // locks get killed every 10
const TIMEOUT_SECONDS = 72000
const MAX_RENEW = TIMEOUT_SECONDS / RENEW_EVERY_X_SEC // renews every 5 minutes
// const WARN_RENEW = 45 / RENEW_EVERY_X_MIN
interface LockInfo {
    CurrentUser: string
    HasLock: boolean
    LockAcquired: Date
    LockOwner: string
    LockKey: string
}
type LockInfoError = string

type AcquireLockReturn = LockInfo | LockInfoError
export interface AcquireLockParams {
    LockKey: string
    AppName: string
}
export type RenewLockParams = AcquireLockParams

const acquireLock = async (params: AcquireLockParams): Promise<AcquireLockReturn> => {
    let url = getAPIURL() + 'lock/AcquireLock/'
    let req = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(params),
    })
    if (req.ok) {
        let jsonTable = await req.json()
        let json = jsonTable[0]
        json.LockKey = params.LockKey
        fqAlert({ logLevel: 'debug', type: 'warning', message: `got lock ${JSON.stringify(json)}` })
        return json
    } else {
        return req.statusText
    }
}

const renewLock = async (params: AcquireLockParams): Promise<AcquireLockReturn> => {
    let url = getAPIURL() + 'lock/RenewLock/'
    let req = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(params),
    })
    if (req.ok) {
        let jsonTable = await req.json()
        let json = jsonTable[0]
        json.LockKey = params.LockKey
        fqAlert({
            logLevel: 'debug',
            type: 'warning',
            message: `renewed lock ${JSON.stringify(json)}`,
        })

        return json
    } else {
        return req.statusText
    }
}

const releaseLock = async (params: AcquireLockParams): Promise<boolean> => {
    let url = getAPIURL() + 'lock/ReleaseLock/'
    let req = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        // assures that this stays alive if browser closes while this was in process of sending
        keepalive: true,
        credentials: 'include',
        body: JSON.stringify(params),
    })
    fqAlert({ logLevel: 'debug', type: 'warning', message: `released lock for ${params.LockKey}` })

    return req.ok
}

const isInBatch = async (orderNumber: string) => {
    let url = getAPIURL() + 'order/isInBatch/'
    let req = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ orderNumber }),
    })
    // assuming it's in batch if it failed
    if (!req.ok) return true
    else {
        let json = await req.json()
        return !!json
    }
}
interface AcquireOrderLockParams {
    orderNumber: string
}
export const acquireOrderLock = createAsyncThunk<
    LockInfo,
    AcquireOrderLockParams,
    { state: RootState }
>('lock/acquireOrderLock', async (payload: AcquireOrderLockParams, thunkAPI) => {
    const { orderNumber } = payload
    let state = thunkAPI.getState().meta
    let cantAcquireLock = await isInBatch(orderNumber)
    if (cantAcquireLock) {
        return thunkAPI.rejectWithValue(`Order #${orderNumber} is currently being sent`)
    }
    let newLock = await acquireLock({ LockKey: orderNumber, AppName: ORDER_ENTRY_APPNAME })
    if (typeof newLock === 'string') {
        return thunkAPI.rejectWithValue(`Could not acquire lock for Order #${orderNumber}`)
    } else {
        let currentLock = state.lock
        if (currentLock) {
            releaseLock({ LockKey: currentLock.LockKey, AppName: ORDER_ENTRY_APPNAME })
        }
        return newLock
    }
})

export const renewOrderLock = createAsyncThunk<LockInfo, undefined, { state: RootState }>(
    'lock/renewOrderLock',
    async (_, thunkAPI) => {
        let state = thunkAPI.getState().meta
        let currentLock = state.lock!
        let req = await renewLock({ LockKey: currentLock.LockKey, AppName: ORDER_ENTRY_APPNAME })
        if (typeof req === 'string') {
            // Error
            return thunkAPI.rejectWithValue(req)
        } else {
            // lost lock
            if (!req.HasLock) {
            }
            return req
        }
    }
)

export const releaseOrderLock = createAsyncThunk<boolean, undefined, { state: RootState }>(
    'lock/releaseOrderLock',
    async (_, thunkAPI) => {
        let state = thunkAPI.getState().meta
        let currentLock = state.lock
        if (currentLock) {
            let req = await releaseLock({
                LockKey: currentLock.LockKey,
                AppName: ORDER_ENTRY_APPNAME,
            })
            return req
        }
        return true
    }
)

export const forceReleaseOrderLock = createAsyncThunk<boolean, undefined, { state: RootState }>(
    'lock/forceReleaseOrderLock',
    async (_, thunkAPI) => {
        let state = thunkAPI.getState().meta
        let currentLock = state.lock
        if (currentLock) {
            try {
                await releaseLock({
                    LockKey: currentLock.LockKey,
                    AppName: ORDER_ENTRY_APPNAME,
                })
            } finally {
                return true
            }
        }
        return true
    }
)
setInterval(async () => {
    let { lock, renewsSinceLastChange } = store.getState().meta
    // if there hasn't been a change for MAX_RENEW renews  force release the lock
    if (lock && lock.HasLock && renewsSinceLastChange >= MAX_RENEW) {
        store.dispatch(forceReleaseOrderLock())
        fqAlert({
            type: 'error',
            title: `Warning: You have not made changes to ${lock.LockKey} for over 20 hours. 
        \nTickets will not print or confirm if an order is open in change mode!`,
        })
        return
    }
    //     // if we have the lock try to renew it every 45 seconds
    if (lock && lock.HasLock && renewsSinceLastChange < MAX_RENEW) {
        let returnedLock = await store.dispatch(renewOrderLock())
        if (isFulfilled(returnedLock)) {
            if (!returnedLock.payload.HasLock) {
                store.dispatch(forceReleaseOrderLock())
                fqAlert({
                    type: 'error',
                    title: `You lost your lock on Order ${lock.LockKey}.`,
                })
            }
        } else {
            store.dispatch(forceReleaseOrderLock())
            fqAlert({
                type: 'error',
                title: `You lost your lock on Order ${lock.LockKey}.`,
            })
        }
    }
}, RENEW_EVERY_X_SEC * 1000)

export interface LockState {}

const initialState: LockState = {}

export const lockSlice = createSlice({
    name: 'lock',
    initialState,
    reducers: {},
})

export default lockSlice.reducer
